import React, { useState, useEffect, useCallback } from 'react';
import { Plus, X, ShieldCheck, Clock } from 'lucide-react';
import { Button } from './Button';
import { Pill } from './Pill';
import * as api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

/**
 * SsoDomainsPanel — manage the email domains a sign-in provider owns.
 *
 * A verified domain is globally unique to one provider, so a work email at that domain routes to
 * exactly this provider at sign-in (no picker). Verification is proof of DNS control: the operator
 * publishes the shown TXT record, then verifies. Unverified claims and the provider's allow-list
 * still cover shared consumer domains. Reuses the backend domain API verbatim.
 */
function firstArray(res, key) {
  const d = res && (res.data !== undefined ? res.data : res);
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d[key])) return d[key];
  return [];
}

export default function SsoDomainsPanel({ providerId }) {
  const { addToast } = useToast();
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [newDomain, setNewDomain] = useState('');

  const reload = useCallback(async () => {
    if (!providerId) { setDomains([]); return; }
    setLoading(true);
    try {
      const r = await api.listSsoDomains(providerId);
      setDomains(firstArray(r, 'domains'));
    } catch (e) {
      addToast({ tone: 'danger', title: 'Could not load domains', description: e?.message });
    } finally {
      setLoading(false);
    }
  }, [providerId, addToast]);

  useEffect(() => { reload(); }, [reload]);

  const claim = async () => {
    const domain = newDomain.trim();
    if (!domain) { addToast({ tone: 'danger', title: 'Enter a domain' }); return; }
    setBusy(true);
    try {
      const res = await api.claimSsoDomain(providerId, domain);
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Could not add domain', description: res.error || res.data?.error });
        return;
      }
      setNewDomain('');
      await reload();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Could not add domain', description: e?.message });
    } finally { setBusy(false); }
  };

  const verify = async (claimId) => {
    setBusy(true);
    try {
      const res = await api.verifySsoDomain(claimId);
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Not verified yet', description: res.error || res.data?.error });
        return;
      }
      addToast({ tone: 'success', title: 'Domain verified' });
      await reload();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Not verified yet', description: e?.message });
    } finally { setBusy(false); }
  };

  const remove = async (claimId) => {
    setBusy(true);
    try {
      const res = await api.removeSsoDomain(claimId);
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Could not remove domain', description: res.error || res.data?.error });
        return;
      }
      await reload();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Could not remove domain', description: e?.message });
    } finally { setBusy(false); }
  };

  const inputCls = 'v2-focus-ring flex-1 bg-v2-surface text-v2-ink border border-v2-border rounded-v2-md px-3 py-1.5 text-[12.5px] font-mono disabled:opacity-50';

  return (
    <div className="space-y-3">
      <p className="text-[12px] text-v2-ink-muted m-0">
        A verified domain routes its users straight to this provider. Add a domain, publish the TXT
        record shown, then verify.
      </p>

      {loading ? (
        <p className="text-v2-ink-muted text-[12.5px] m-0">Loading…</p>
      ) : domains.length === 0 ? (
        <p className="text-v2-ink-muted text-[12.5px] m-0">No domains yet.</p>
      ) : (
        <ul className="space-y-2">
          {domains.map((d) => (
            <li key={d.id} className="rounded-v2-md border border-v2-border px-3 py-2 space-y-1.5">
              <div className="flex items-center gap-2 text-[12.5px]">
                <span className="font-semibold text-v2-ink font-mono">{d.domain}</span>
                {d.verified
                  ? <Pill tone="success" size="sm"><ShieldCheck size={11} className="inline mr-0.5" />Verified</Pill>
                  : <Pill tone="warning" size="sm"><Clock size={11} className="inline mr-0.5" />Pending</Pill>}
                <span className="ml-auto flex items-center gap-1.5">
                  {!d.verified && <Button size="xs" variant="secondary" disabled={busy} onClick={() => verify(d.id)}>Verify</Button>}
                  <Button size="xs" variant="ghost" disabled={busy} onClick={() => remove(d.id)} iconLeft={<X size={13} />} aria-label="Remove domain" />
                </span>
              </div>
              {!d.verified && (
                <div className="text-[11px] text-v2-ink-muted leading-snug">
                  Add this DNS TXT record, then Verify:
                  <div className="mt-1 rounded bg-v2-surface-sub border border-v2-border px-2 py-1 font-mono text-[10.5px] text-v2-ink break-all">
                    {d.challengeHost} &nbsp;TXT&nbsp; {d.txtRecord}
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center gap-2">
        <input className={inputCls} value={newDomain} disabled={busy}
          onChange={(e) => setNewDomain(e.target.value)} placeholder="acme.com"
          onKeyDown={(e) => { if (e.key === 'Enter') claim(); }} />
        <Button size="sm" variant="secondary" iconLeft={<Plus size={14} />} disabled={busy} onClick={claim}>Add domain</Button>
      </div>
    </div>
  );
}
